import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SurveyTemplate from "./SurveyTemplate";
import InfoAccordion from "./InfoAccordion";
import "../styles/components/MultiPageSurvey.css";

const SurveyPage3 = ({
  surveyData,
  updateSurveyData,
  onNext,
  onBack,
  onNavigationBack,
  currentSection,
  initialQuestionIndex = 0,
  updatePageState,
  pageNumber,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(initialQuestionIndex);
  const [localData, setLocalData] = useState({
    familyCancerHistory: surveyData.familyCancerHistory || [],
  });

  const updatePageStateRef = useRef(updatePageState);
  updatePageStateRef.current = updatePageState;
  const lastSyncedQuestion = useRef(null);
  useEffect(() => {
    if (lastSyncedQuestion.current === currentQuestion || !updatePageStateRef.current || !pageNumber) return;
    lastSyncedQuestion.current = currentQuestion;
    updatePageStateRef.current(pageNumber, currentQuestion);
  }, [currentQuestion, pageNumber]);

  const cancerTypes = [
    {
      value: "lung",
      label: t("survey.page3.familyCancerHistory.options.lung"),
    },
    {
      value: "gastrointestinal",
      label: t("survey.page3.familyCancerHistory.options.gastrointestinal"),
    },
    {
      value: "liver",
      label: t("survey.page3.familyCancerHistory.options.liver"),
    },
    {
      value: "cervical",
      label: t("survey.page3.familyCancerHistory.options.cervical"),
    },
    {
      value: "breast",
      label: t("survey.page3.familyCancerHistory.options.breast"),
    },
    {
      value: "bowel",
      label: t("survey.page3.familyCancerHistory.options.bowel"),
    },
    {
      value: "prostate",
      label: t("survey.page3.familyCancerHistory.options.prostate"),
    },
    {
      value: "thyroid",
      label: t("survey.page3.familyCancerHistory.options.thyroid"),
    },
    {
      value: "skin",
      label: t("survey.page3.familyCancerHistory.options.skin"),
    },
    {
      value: "ovarian",
      label: t("survey.page3.familyCancerHistory.options.ovarian"),
    },
    {
      value: "uterine",
      label: t("survey.page3.familyCancerHistory.options.uterine"),
    },
    {
      value: "testicular",
      label: t("survey.page3.familyCancerHistory.options.testicular"),
    },
    {
      value: "none",
      label: t("survey.page3.familyCancerHistory.options.none"),
    },
  ];

  const handleCancerSelection = (cancerType) => {
    setLocalData((prev) => {
      let currentHistory = [...prev.familyCancerHistory];

      if (cancerType === "none") {
        // If "none" is selected, clear all other selections
        if (currentHistory.includes("none")) {
          // Remove "none" if already selected
          currentHistory = currentHistory.filter((item) => item !== "none");
        } else {
          // Select only "none" and clear everything else
          currentHistory = ["none"];
        }
      } else {
        // If a specific cancer type is selected
        if (currentHistory.includes("none")) {
          // Remove "none" if it was previously selected
          currentHistory = currentHistory.filter((item) => item !== "none");
        }

        const index = currentHistory.indexOf(cancerType);
        if (index > -1) {
          // Remove if already selected
          currentHistory.splice(index, 1);
        } else {
          // Add if not selected
          currentHistory.push(cancerType);
        }
      }

      return {
        ...prev,
        familyCancerHistory: currentHistory,
      };
    });
  };

  const handleNext = () => {
    updateSurveyData("familyCancerHistory", localData.familyCancerHistory);
    onNext();
  };

  const handleHeaderBack = () => {
    // Header back button goes to SurveyPage2
    navigate("/survey/2");
  };

  const handleNavigationBack = () => {
    // Navigation back button goes to previous page (SurveyPage2)
    if (onNavigationBack) {
      onNavigationBack();
    }
  };

  const surveyConfig = {
    introText: t("survey.page3.intro"),
    questionText: t("survey.page3.familyCancerHistory.question"),
    options: [],
    infoSections: [
      {
        title: t("survey.page3.whyFamilyHistoryMatters"),
        content: t("survey.page3.familyHistoryContent"),
      },
      {
        title: t("survey.page3.closeFamilyIncludes"),
        content: t("survey.page3.closeFamilyContent"),
      },
    ],
  };

  return (
    <div className="survey-page-3">
      <SurveyTemplate
        introText={surveyConfig.introText}
        questionText={surveyConfig.questionText}
        options={surveyConfig.options}
        infoSections={surveyConfig.infoSections}
        onOptionSelect={() => {}}
        selectedOption=""
        onBack={onBack}
        infoPanelLayout="sidepanel"
        customInput={
          <div className="cancer-selection-wrapper">
            <div className="cancer-options-grid">
              {cancerTypes.map((cancer) => (
                <button
                  key={cancer.value}
                  className={`cancer-option-button ${
                    localData.familyCancerHistory.includes(cancer.value)
                      ? "selected"
                      : ""
                  }`}
                  onClick={() => handleCancerSelection(cancer.value)}
                >
                  <span>{cancer.label}</span>
                  {localData.familyCancerHistory.includes(cancer.value) && (
                    <span className="checkmark">✓</span>
                  )}
                </button>
              ))}
            </div>

            <InfoAccordion trigger={t("survey.page3.familyCancerHistory.options.bowel") + " vs " + t("survey.page3.familyCancerHistory.options.gastrointestinal") + "?"}>
              <p>
                <strong>{t("survey.page3.familyCancerHistory.options.gastrointestinal")}</strong>
                {" — "}
                {t("survey.page3.giVsBowel.giExplanation", { defaultValue: "Includes the entire digestive tract — esophagus, stomach, small intestine, liver, pancreas, and colon." })}
              </p>
              <p>
                <strong>{t("survey.page3.familyCancerHistory.options.bowel")}</strong>
                {" — "}
                {t("survey.page3.giVsBowel.bowelExplanation", { defaultValue: "Specifically means colon & rectum (large intestine) only. If a family member had stomach cancer, choose GI instead." })}
              </p>
              <p style={{ fontSize: "13px", opacity: 0.8 }}>
                💡 {t("survey.page3.giVsBowel.hint", { defaultValue: "Not sure which? Select both — we'll factor in the broader risk." })}
              </p>
            </InfoAccordion>

            <div className="selection-summary">
              {localData.familyCancerHistory.length > 0 ? (
                <p className="selected-count">
                  {t("survey.page3.selectionSummary.selected", {
                    count: localData.familyCancerHistory.length,
                  })}
                </p>
              ) : (
                <p className="no-selection">
                  {t("survey.page3.selectionSummary.none")}
                </p>
              )}
            </div>
          </div>
        }
        progressContainer={
          <div className="progress-container">
            {/* Section Progress */}
            <div className="section-progress">
              <div className="section-indicators">
                <div
                  className={`section-indicator ${
                    currentSection >= 1 ? "active" : ""
                  }`}
                >
                  <span className="section-number">1</span>
                  <span className="section-label">
                    {t("survey.sections.demographics")}
                  </span>
                </div>
                <div
                  className={`section-indicator ${
                    currentSection >= 2 ? "active" : ""
                  }`}
                >
                  <span className="section-number">2</span>
                  <span className="section-label">
                    {t("survey.sections.physical")}
                  </span>
                </div>
                <div
                  className={`section-indicator ${
                    currentSection >= 3 ? "active" : ""
                  }`}
                >
                  <span className="section-number">3</span>
                  <span className="section-label">
                    {t("survey.sections.family")}
                  </span>
                </div>
                <div
                  className={`section-indicator ${
                    currentSection >= 4 ? "active" : ""
                  }`}
                >
                  <span className="section-number">4</span>
                  <span className="section-label">
                    {t("survey.sections.symptoms")}
                  </span>
                </div>
                <div
                  className={`section-indicator ${
                    currentSection >= 5 ? "active" : ""
                  }`}
                >
                  <span className="section-number">5</span>
                  <span className="section-label">
                    {t("survey.sections.lifestyle")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        }
        navigationButtons={
          <div className="navigation-buttons">
            <button
              className="nav-button back-button"
              onClick={handleNavigationBack}
            >
              Back
            </button>
            <button className="nav-button next-button" onClick={handleNext}>
              Continue
            </button>
          </div>
        }
      />
    </div>
  );
};

export default SurveyPage3;
